import { useEffect, useMemo, useState, Fragment } from "react";
import Button from "react-bootstrap/Button";
import { v4 as uuidv4 } from "uuid";
import { POLICY_COMPONENT_TYPE_OPTIONS } from "@constants/policy-component-type-options";
import { resources } from "@constants/hardcoded-strings";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PolicyConfigDecisionAttributeFieldResultTypes } from "@enums/policy-config-decision-attribute-field-result-type";
import { TriggerTypes } from "@enums/trigger-types";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { CreatePolicyComponentTriggerConfigurationValidationErrors } from "@idw-types/common/validation/create-policy-component/create-policy-component-validation-error";
import { AxnAttributeType, AxnAttributeTypeField } from "@idw-types/data/reference-data/axn";
import { AvailableAssertionStatus, DeployedPolicyComponent } from "@idw-types/data/reference-data/generic";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { FormattedPolicyComponent, FormattedPolicyComponentCondition } from "@idw-types/data/reference-data/generic/policy-component";
import { FormattedRpServiceConfigurationServiceOffering, FormattedRpServiceConfigurationServiceOfferingAssertion } from "@idw-types/data/rp-service-configuration";
import { CreatePolicyComponentConditionContainer } from "./create-policy-component-condition-container/create-policy-component-condition-container";
import styles from "./create-policy-component-trigger-configuration-container.less";

type CreatePolicyComponentTriggerConfigurationContainerProps = {
    policyComponentState: FormattedPolicyComponent;
    allPolicyComponents: Array<DeployedPolicyComponent>;
    assertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>;
    availableAssertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    allAxnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypes: Array<AxnAttributeType>;
    allAxnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAcquiredAttributeTypeFields: Array<AxnAttributeTypeField>;
    policyComponents: Array<DeployedPolicyComponent>;
    acquiredAttributes: Array<AxnAcquiredAttribute>;
    validationErrors: CreatePolicyComponentTriggerConfigurationValidationErrors;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    setPolicyComponentState: (policyComponentState: FormattedPolicyComponent) => void;
}

export const CreatePolicyComponentTriggerConfigurationContainer = (props: CreatePolicyComponentTriggerConfigurationContainerProps) => {
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [layout] = useState(LayoutTypes.ROW);
    const triggerConfigurationResources = resources.modals.createPolicyComponent.steps.triggerConfiguration;
    const {
        policyComponentState,
        assertions,
        allPolicyComponents,
        availableAssertions,
        serviceOfferings,
        allAxnAttributeTypes,
        axnAttributeTypes,
        allAxnAttributeTypeFields,
        axnAttributeTypeFields,
        axnAcquiredAttributeTypeFields,
        policyComponents,
        acquiredAttributes,
        validationErrors,
        axnAvailableAssertionStatuses,
        setPolicyComponentState
    } = props;


    const handlePolicyComponentTypeChange = (selectedOption: SelectOption<string>) => {
        setPolicyComponentState({
            ...policyComponentState,
            type: selectedOption.value
        });
    }

    const setCondition = (newCondition: FormattedPolicyComponentCondition) => {
        const updatedConditions = policyComponentState.conditions.map((condition) => {
            if (condition.uuid === newCondition.uuid) {
                return newCondition;
            }

            return condition;
        });

        setPolicyComponentState({
            ...policyComponentState,
            conditions: updatedConditions
        });
    }

    const addCondition = () => {
        setPolicyComponentState({
            ...policyComponentState,
            conditions: [
                ...policyComponentState.conditions,
                {
                    assertionID: null,
                    attributeTypeID: null,
                    attributeFieldID: null,
                    componentID: null,
                    id: null,
                    policyComponentID: null,
                    policyConditionOperator: PolicyConfigDecisionAttributeFieldResultTypes.EQUAL_TO,
                    result: null,
                    serviceOfferingID: null,
                    values: null,
                    uuid: uuidv4(),
                    triggerType: TriggerTypes.ASSERTION
                }
            ]
        });
    }

    const removeCondition = (uuid: string) => {
        const updatedConditions = policyComponentState.conditions.filter(condition => {
            return (condition.uuid !== uuid);
        });

        setPolicyComponentState({
            ...policyComponentState,
            conditions: updatedConditions
        });
    }

    useEffect(() => {
        if (policyComponentState.conditions.length === 0) {
            addCondition();
        }
    }, []);

    const selectedPolicyComponentTypeOption = useMemo(() => {
        return POLICY_COMPONENT_TYPE_OPTIONS.find(option => option.value === policyComponentState.type);
    }, [POLICY_COMPONENT_TYPE_OPTIONS, policyComponentState.type]);

    return (
        <div id="creation-policy-component-trigger-configuration">
            <span className={styles.title}>
                {triggerConfigurationResources.title}
            </span>
            <div>
                <SelectFormGroup
                    automationId={`creation-policy-component-trigger-configuration-component-type-select`}
                    controlId={`creation-policy-component-trigger-configuration-component-type-select`}
                    label={triggerConfigurationResources.inputs.componentType.label}
                    ariaLabel={triggerConfigurationResources.inputs.componentType.label}
                    onChange={handlePolicyComponentTypeChange}
                    options={POLICY_COMPONENT_TYPE_OPTIONS}
                    selectedOption={selectedPolicyComponentTypeOption}
                    disabled={false}
                    isRequired={true}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                    layout={layout}
                />
                <hr />
                <div>
                    {validationErrors.atLeastOneConditionAddedError}
                    {policyComponentState.conditions.map((condition, index) =>
                        <Fragment key={`${condition.uuid}-${index}`}>
                            <div>
                                <CreatePolicyComponentConditionContainer
                                    index={index}
                                    isDisabled={false}
                                    condition={condition}
                                    conditions={policyComponentState.conditions}
                                    allAxnAttributeTypeFields={allAxnAttributeTypeFields}
                                    axnAttributeTypeFields={axnAttributeTypeFields}
                                    axnAcquiredAttributeTypeFields={axnAcquiredAttributeTypeFields}
                                    allPolicyComponents={allPolicyComponents}
                                    policyComponents={policyComponents}
                                    allAxnAttributeTypes={allAxnAttributeTypes}
                                    axnAttributeTypes={axnAttributeTypes}
                                    validationErrors={validationErrors.conditionErrors[condition.uuid]}
                                    assertions={assertions}
                                    availableAssertions={availableAssertions}
                                    serviceOfferings={serviceOfferings}
                                    acquiredAttributes={acquiredAttributes}
                                    axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                                    setCondition={setCondition}
                                    removeCondition={removeCondition}
                                />
                            </div>
                            {index !== policyComponentState.conditions.length - 1 && selectedPolicyComponentTypeOption && (
                                <div className={styles.policyComponentTypeOptionText}>
                                    <label>{selectedPolicyComponentTypeOption.value}</label>
                                </div>
                            )}
                        </Fragment>
                    )}
                    <div className={styles.optionContainer}>
                        <Button id="creation-component-trigger-configuration-add-trigger-button" variant="primary" onClick={addCondition}>{"Add a trigger"}</Button>
                    </div>
                </div>
            </div>
        </div>
    );
}